import React, { CSSProperties, useEffect, useMemo, useState } from 'react';

import { t } from 'i18next';
import _ from 'lodash';
import { DateTime } from 'luxon';

import {
  useGetMarksQuery,
  useGetStudentsForAssignmentQuery,
  useUpdateMarksForStudentMutation,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { ReactComponent as AttachIcon } from 'assets/icons/attachment-new.svg';
import { ReactComponent as CommentIcon } from 'assets/icons/comment-new.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/arrow-right-new.svg';
import { ReactComponent as Floppy } from 'assets/icons/floppy-new.svg';
import { ReactComponent as DateIcon } from 'assets/icons/datepicker-new.svg';
import Button from 'components/Button/Button';
import StyledTooltip from 'components/StyledTooltip/StyledTooltip';
import ModalDialog from 'components/ModalDialog/ModalDialog';

import './GradesModal.scoped.scss';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  openModal?: boolean;
  handleModal?: () => void;
  assignment?: any;
  onInputChange?: (id: string, mark: number) => void;
}

const GradesModal: React.FC<Props> = ({ openModal, handleModal, assignment }) => {
  const [updateMarksForStudentMutation] = useUpdateMarksForStudentMutation();
  const [students, setStudents] = useState([]);
  const [studentUpdate, setStudentUpdate] = useState([]);

  const { data, loading } = useGetStudentsForAssignmentQuery({
    variables: {
      _eq: assignment?.id,
    },
    fetchPolicy: 'network-only',
  });

  const assignmentUsersOrdered = useMemo(() => {
    if (!data || !data.assignment_user) {
      return [];
    }
    return _.orderBy(data.assignment_user, ['user.full_name']);
  }, [data]);

  const marks = useGetMarksQuery();

  useEffect(() => {
    if (!loading) {
      const dataAssignment = assignmentUsersOrdered.map(obj => {
        const mark = {
          label: obj.mark ? obj.mark.name : '',
          value: obj.mark_id,
        };
        return {
          ...obj,
          mark,
        };
      });
      setStudents(dataAssignment);
      setStudentUpdate(dataAssignment);
    }
  }, [loading, handleModal]);

  function setMarkData(key, prop, value) {
    const items = JSON.parse(JSON.stringify(students));
    items[key][prop] = value;
    setStudents(items);
  }

  function save() {
    _.filter(students, function (obj) {
      return (
        (obj.mark.value != null && obj.mark.value !== '') ||
        (obj.comment !== null && obj.comment !== '')
      );
    }).forEach(obj => {
      updateMarksForStudentMutation({
        variables: {
          id: { _eq: obj.id }, // value for 'id'
          mark_id: obj.mark.value, // value for 'mark_id'
          comment: obj.comment, // value for 'comment'
        },
      });
    });

    handleModal();
  }

  const markOptionsFormatting = () => {
    let formattedMarks = [];

    if (marks && marks.data) {
      formattedMarks = _.orderBy(marks.data.marks, 'name').map(obj => {
        return {
          label: obj.name,
          value: obj.id,
        };
      });
    }
    return formattedMarks;
  };

  const changeMark = (e, key) => {
    const input = e.target.value;
    const tempMark = markOptionsFormatting();

    tempMark.forEach(item => {
      if (input === item.label) {
        setMarkData(key, 'mark', { ...item, value: item.value });
      }
    });
  };

  return (
    <ModalDialog
      open={openModal}
      onClose={handleModal}
      title={t('live_classes:EditGrades')}
      hasContentPadding={false}
    >
      <div className="grading-main-container">
        <div className="grading-top-section">
          <div className="grading-ts-left">
            <p className="th-title">{t('grades:Assignment')}</p>
            <span>{assignment?.title}</span>
          </div>
          <div className="grading-ts-middle">
            <p className="th-title">{t('common:Date')}</p>
            <div className="date-icon-container">
              <DateIcon />
              <span>{DateTime.fromISO(assignment?.due_at).toFormat('dd.MM.y')}</span>
            </div>
          </div>
          <div className="grading-ts-right">
            <p className="th-title">
              {t('common:Type')}: <span>{assignment?.assignment_type?.name}</span>
            </p>
            <p className="th-title">
              {t('common:Teacher')}: <span>{assignment?.user?.full_name}</span>
            </p>
            <p className="th-title">
              {t('common:Date')}:
              <span>{DateTime.fromISO(assignment?.created_at).toFormat('dd.MM.y')}</span>
            </p>
          </div>
        </div>
        <div className="grading-container">
          <table className="grading-table">
            <thead>
              <tr className="tr-1">
                <th className="th-1">
                  <p className="th-title tr-title">{t('live_classes:student')}</p>
                </th>
                <th className="th-2">
                  <p className="th-title tr-title">{t('common:Grade')}</p>
                </th>
                <th className="th-3">
                  <p className="th-title tr-title">{t('study:Comment')}</p>
                </th>
                <th className="th-4"></th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                studentUpdate?.map((obj, key) => {
                  return (
                    <tr key={obj.id}>
                      <td className="th-1">
                        <p>{obj?.user?.full_name}</p>
                      </td>

                      <td className="th-2 td-2">
                        <div className="grade-section">
                          <div className="grade-el-1">
                            <p>{obj?.mark?.label !== '' ? obj?.mark?.label : '/'}</p>
                            <StyledTooltip
                              direction="up"
                              tagName="div"
                              content={<p>{obj?.comment}</p>}
                            >
                              <div className="dropdown-dots pointer">
                                <CommentIcon />
                              </div>
                            </StyledTooltip>
                          </div>
                          <div className="grade-el-2">
                            <ArrowRight className="arrow-right" />
                          </div>
                          <div className="grade-el-1">
                            <input className="grade-input" onChange={e => changeMark(e, key)} />
                          </div>
                        </div>
                      </td>
                      <td className="th-1 td-2">
                        <input
                          className="comment-input"
                          placeholder={t('study:Comment')}
                          value={students.find(student => student.id === obj.id)?.comment || ''}
                          onChange={e => {
                            setMarkData(key, 'comment', e.target.value);
                          }}
                        />
                      </td>
                      <td className="th-4">
                        <AttachIcon />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          <datalist id="marks">
            {!marks.loading &&
              marks.data.marks.map(obj => {
                return (
                  <option className={'p-1 cursor-pointer text hover-text-bold'} key={obj.id}>
                    {obj.name}
                  </option>
                );
              })}
          </datalist>
        </div>

        <div className="footer-buttons">
          {/* this button will be hidden for now, due to Henris wish */}
          {/* <Button
            onClick={handleModal}
            outlined
            round
            style={{
              background: '#5cd79e 0% 0% no-repeat padding-box',
              height: 30,
              fontSize: 12,
              paddingLeft: '15px',
              paddingRight: '15px',
              color: '#fff',
              fontWeight: 600,
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="inside-button">
              <ArrowCircleRight className="button-img" />
              {t('common:save_and_publish')}
            </div>
          </Button> */}
          <Button
            onClick={save}
            outlined
            round
            style={{
              background: '#5cd79e 0% 0% no-repeat padding-box',
              height: 30,
              fontSize: 12,
              paddingLeft: '15px',
              paddingRight: '15px',
              color: '#fff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="inside-button">
              <Floppy className="button-img" />
              {t('common:save')}
            </div>
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
};

export default GradesModal;
